/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import orderBy from 'lodash.orderby';
import { inBetween, partiallyOverlap } from 'src/luis/modules/labeling/utils/generalUtils';
import { CompositeEntity } from 'src/luis/projects/entities/types/CompositeEntity';
import { Entity } from 'src/luis/projects/entities/types/Entity';
import { EntityRole } from 'src/luis/projects/entities/types/EntityRole';
import { SimpleEntity } from 'src/luis/projects/entities/types/SimpleEntity';
import { isEntityOfType, isMachineLearned } from 'src/luis/projects/entities/utils/entityHelpers';
import { getChildByIdHelper } from 'src/luis/projects/entities/utils/simpleEntityHelpers/simpleEntityHelpers';
import { EntityLabel } from 'src/luis/projects/utterances/types/Utterance';
import { ModelType } from 'src/luis/types/ModelType';
import { Label } from 'src/modules/labeling/types/Label';

type TransformLabelsConfig = {
    enableConflicts: boolean;
    hideSubEntities: boolean;
    hidePredictions: boolean;
    hideRegexAndPrebuilt: boolean;
};

type TransformLabelsInput = {
    /**
     * Array of utterance tokens.
     */

    tokenizedText: string[];

    /**
     * Array of utterance user labels.
     */

    userLabels: EntityLabel[];

    /**
     * Array of utterance user predictions.
     */
    predictions: EntityLabel[];

    /**
     * Whether the utterance is trained or not.
     */
    config?: TransformLabelsConfig;
};

export type TransformLabelsReturn = {
    /**
     * Labels to be fed into the Labeler component.
     */
    labels: Label[];

    /**
     * Array of booleans indicating the state of every token whether it has a conflict or not.
     */
    conflicts: boolean[];
};

/**
 * Internal type, exported only for tests.
 */
export type EnhancedLabel = Label & {
    children: EnhancedLabel[];
    predicted?: boolean;
    isSoftDeleted: boolean;
    pairedLabel?: {
        start: number;
        end: number;
        depth: number;
    };
};

type EnhancedEntityLabel = Readonly<
    EntityLabel & {
        predicted?: boolean;
        isSoftDeleted?: boolean;
    }
>;

/**
 * Transforms user labels and predictions into a data structure that's compatible with
 * Labeler component.
 */
export const transformLabels = (data: TransformLabelsInput): TransformLabelsReturn => {
    const {
        tokenizedText,
        config = { enableConflicts: false, hideRegexAndPrebuilt: false, hidePredictions: false, hideSubEntities: false }
    } = data;
    let userLabels = data.userLabels.slice();
    let predictions = data.predictions.slice();
    let conflicts = tokenizedText.map(_ => false);

    // Soft delete predictions that have corresponding labels,
    // and add predicted property on labels that have predictions
    softDeleteRedundantPredictions(userLabels, predictions);

    // Convert non-ML entities to labels
    userLabels = [...userLabels, ...predictions.filter(({ entity: { typeId }, role }) => !isMachineLearned(typeId) && !role)];
    predictions = predictions.filter(({ entity: { typeId }, role }) => isMachineLearned(typeId) || role);

    // Convert to labeling data structure
    let transformedUserLabels = toLabels(userLabels);
    let transformedPredictions = toLabels(predictions, true);

    // Assign predictions and labels children
    assignChildren(transformedUserLabels);
    assignChildren(transformedPredictions);

    // Calculate depth
    calculateDepth(transformedUserLabels);
    calculateDepth(transformedPredictions);

    // Labels and predictions have to be filtered before calculating the ui properties.
    filterLabels(transformedUserLabels, config);
    filterLabels(transformedPredictions, config);

    // Calculate labels and predictions compounds
    calculatePairedLabels(transformedUserLabels, transformedPredictions);

    // Assign levels
    transformedUserLabels = assignLevels(transformedUserLabels);
    transformedPredictions = assignLevels(transformedPredictions);

    // Hard delete soft deleted labels and predictions
    hardDeleteSoftDeletedLabels(transformedUserLabels);
    hardDeleteSoftDeletedLabels(transformedPredictions);

    // Check for conflicts
    if (config.enableConflicts) {
        conflicts = checkForConflicts(tokenizedText, [transformedUserLabels, transformedPredictions]);
    }

    transformedUserLabels = flattenLabels(transformedUserLabels);
    transformedPredictions = flattenLabels(transformedPredictions);

    transformedUserLabels = fromZeroToOneBasedLevels(transformedUserLabels);
    transformedPredictions = fromZeroToOneBasedLevels(transformedPredictions);

    return { labels: [...transformedUserLabels, ...transformedPredictions], conflicts };
};

/// ///////////////////////// Converting to Label DS ////////////////////////////

const toLabels = (entityLabels: EnhancedEntityLabel[], isPrediction: boolean = false): EnhancedLabel[] =>
    entityLabels.map(label => ({
        ...label,
        renderedAs: isPrediction ? 'box' : 'line',
        start: label.startTokenIndex,
        end: label.endTokenIndex,
        depth: 1,
        renderingLevel: 0,
        entity: label.entity,
        role: label.role,
        children: [],
        predicted: label.predicted,
        isSoftDeleted: label.isSoftDeleted ?? false,
        intrinsicStart: label.startTokenIndex,
        intrinsicEnd: label.endTokenIndex
    }));

/// ///////////////////////// Removing redundant predictions ////////////////////////////

/**
 * Soft deletes predictions that have corresponding labels, so they can be used by other
 * operations in the pipeline. Soft deleted predictions are not rendered in the UI.
 *
 * @param labels User labels.
 * @param predictions ML predictions.
 */
const softDeleteRedundantPredictions = (labels: EnhancedEntityLabel[], predictions: EnhancedEntityLabel[]): void => {
    for (let i = 0; i < labels.length; i++) {
        const label = labels[i];
        for (let j = 0; j < predictions.length; j++) {
            const prediction = predictions[j];
            // If token spans and entities are the same, and label and prediction have the same role OR
            // the label is for non-ml entity and has a role.
            if (
                sameTokenEntityLabel(label, prediction) &&
                label.entity.id === prediction.entity.id &&
                (isSameRole(label.role, prediction.role) || (!isMachineLearned(label.entity.typeId) && label.role))
            ) {
                labels[i] = { ...label, predicted: true };
                predictions[j] = { ...prediction, isSoftDeleted: true };
            }
        }
    }
};

/// ///////////////////////// Assigning children ////////////////////////////

/**
 * Assigns children to their corresponding parents and removes them from the input array.
 *
 * @param labels User labels or predictions.
 */
export const assignChildren = (labels: EnhancedLabel[]): void => {
    const toBeDeletedLabels: number[] = [];
    for (const parentLabel of labels) {
        const { entity } = parentLabel;
        if (isCompositeParent(entity) || isNDepthParent(entity)) {
            for (let j = 0; j < labels.length; j++) {
                const label = labels[j];
                if (isChildLabel(parentLabel, label)) {
                    label.parent = parentLabel;
                    parentLabel.children.push(label);
                    toBeDeletedLabels.push(j);
                }
            }
        }
    }

    toBeDeletedLabels.sort((a, b) => a - b);

    for (let i = toBeDeletedLabels.length - 1; i >= 0; i--) {
        labels.splice(toBeDeletedLabels[i], 1);
    }
};

/**
 * Returns whether the given label is a child to the parent label or not. By checking token indices and
 * the entity of the child. In case of Composite entity the child might have a role in this case its id
 * will be the id of the role.
 */
const isChildLabel = (parentLabel: EnhancedLabel, childLabel: EnhancedLabel) => {
    const parentEntity = <CompositeEntity | SimpleEntity>parentLabel.entity;
    const parentChildren = <Entity[]>parentEntity.children;

    return (
        (parentChildren.find(childEntity => childEntity.id === childLabel.entity.id) ||
            parentChildren.find(childEntity => childLabel.entity.roles.find(role => role.id === childEntity.id))) &&
        parentLabel.start <= childLabel.start &&
        parentLabel.end >= childLabel.end
    );
};

/// ///////////////////////// Calculating depth ////////////////////////////

/**
 * Assigns each label a depth value.
 *
 * @param labels User labels or predictions
 */
export const calculateDepth = (labels: EnhancedLabel[]): void => {
    labels.forEach(calculateDepthHelper());
};

/**
 * Calculates depth for the given label and calls itself recursively to calculate depth for its children.
 *
 * @param depth Depth value to be assigned to the given label
 */
const calculateDepthHelper = (depth: number = 1) => (label: EnhancedLabel) => {
    label.depth = depth;
    const { entity } = label;

    if (isCompositeParent(entity) || isNDepthParent(entity)) {
        label.children.forEach(calculateDepthHelper(depth + 1));
    }
};

/**
 * Calculates the maximum depth in a label with children.
 */
const getLabelMaxDepth = (label: EnhancedLabel): number => {
    let maxDepth = 0;
    for (const child of label.children) {
        const depth = getLabelMaxDepth(<EnhancedLabel>child);
        maxDepth = Math.max(depth, maxDepth);
    }

    return maxDepth + 1;
};

/// ///////////////////////// Hard delete soft deleted labels ////////////////////////////

/**
 * Recursively deletes soft deleted labels.
 */
export const hardDeleteSoftDeletedLabels = (labels: EnhancedLabel[]) => {
    hardDeleteDfsHelper(labels);
};

/**
 * DFS traversal function. Traverses the labels tree, after it's done
 * traversing each child it checks if the child is soft deleted, if it's
 * soft deleted, it deletes it from the array and adds its children to
 * the array. This way all the non-soft deleted children are approachable.
 */
const hardDeleteDfsHelper = (labels: EnhancedLabel[]) => {
    const childrenOfDeletedLabels: EnhancedLabel[] = [];
    for (let i = labels.length - 1; i >= 0; i--) {
        const label = labels[i];
        hardDeleteDfsHelper(label.children);
        if (label.isSoftDeleted) {
            labels.splice(i, 1);
            label.children.forEach((child: EnhancedLabel) => {
                if (label.pairedLabel) {
                    child.pairedLabel = { ...label.pairedLabel, depth: label.pairedLabel.depth - 1 };
                }
            });
            childrenOfDeletedLabels.push(...(<EnhancedLabel[]>label.children));
        }
    }
    labels.push(...childrenOfDeletedLabels);
};

/// ///////////////////////// Assigning levels ////////////////////////////

/**
 * Changes levels indices from zero-based one-based.
 */
const fromZeroToOneBasedLevels = (labels: EnhancedLabel[]): EnhancedLabel[] =>
    labels.map(l => ({ ...l, renderingLevel: l.renderingLevel + 1 }));

/**
 * Calculates the level of each label and their children. The level property is to be used by the renderers
 * at a later stage to decide how to layout its corresponding label.
 * This algorithm uses an array of Sets each corresponds to a level to facilitate laying out labels.
 *
 * We start by iterating over the labels and the levels sets, if we find that the start token of the current label
 * is already occupied in the level set we continue searching in the next one. If we find an empty spot we layout
 * this label in that level.
 *
 * @param labels User labels or ML predictions.
 */
export const assignLevels = (labels: EnhancedLabel[]): EnhancedLabel[] => {
    /**
     * Order labels based on the following rules:
     * 1) If the the label has a paired label then it should come first, if not then we give precedence to the one with
     * more children.
     * 2) Start token of the label.
     * 3) Alphabetically, based on the entity name.
     */
    labels = orderBy(
        labels,
        [
            (label: EnhancedLabel) => (label.pairedLabel ? Number.MAX_SAFE_INTEGER : label.children.length),
            'start',
            ({ entity: { name } }: EnhancedLabel) => name
        ],
        ['desc', 'asc', 'asc']
    );

    const levels: Set<number>[] = [new Set()];

    for (const label of labels) {
        const level = findNextEmptyLevel(label, levels);
        layoutLabel(label, levels, level);
    }

    return labels;
};

/**
 * Lays out the given label in the current level by occupying its tokens in the current level set.
 * If the label is a parent label, it occupies a rectangle with the width of the parent label and
 * the height of the max depth of the children.
 *
 * @param label Current label to layed out.
 * @param levels Levels sets.
 * @param currentLevel Current level to layout the label in.
 */
const layoutLabel = (label: EnhancedLabel, levels: Set<number>[], currentLevel: number): void => {
    currentLevel = markLabelCompoundDiff(label, levels, currentLevel);

    if (label.children.length > 0) {
        assignChildrenLevels(label, levels);
    } else {
        label.renderingLevel = currentLevel;
        markLevelRangeAsLabeled(label, levels, currentLevel);
    }
};

/**
 * Lays out the given label and all of its descendants.
 *
 * @param levels Levels sets
 * @param currentLevel Current level to layout the label in.
 */
const assignChildrenLevels = (parentLabel: EnhancedLabel, levels: Set<number>[]) => {
    let depthMap = mapDepthToLabels(parentLabel.children);
    depthMap = new Map(orderBy([...depthMap.entries()], ([depth]) => depth, 'desc'));

    // Lay out children based on their depth (the ones closer to the utterance get layed out first)
    depthMap.forEach(labels => {
        const usedLevels = new Set<number>();
        labels.forEach(label => {
            const level = findNextEmptyLevel(label, levels);
            usedLevels.add(level);
            label.renderingLevel = level;
            markLevelRangeAsLabeled(label, levels, level);
        });

        // Mark the levels used by the children of a specific depth so that
        // no other entity can be layed out in the same level inside
        // the parent's boundaries.
        usedLevels.forEach(usedLevel => {
            markLevelRangeAsLabeled(parentLabel, levels, usedLevel);
        });
    });

    const level = findNextEmptyLevel(parentLabel, levels);
    parentLabel.renderingLevel = level;
    markLevelRangeAsLabeled(parentLabel, levels, level);
};

/**
 * Given a label, this function checks the difference between the compound
 * depth calculated earlier and the actual depth of the label if the difference
 * is greater than zero, this means that this label has to preserve some whitespace
 * to match the height of its counterpart in the compound. So this function
 * preserves a number of levels which equals to the diff.
 */
const markLabelCompoundDiff = (label: EnhancedLabel, levels: Set<number>[], currentLevel: number): number => {
    const start = Math.min(label.start, label.pairedLabel?.start);
    const end = Math.min(label.end, label.pairedLabel?.end);
    if (label.pairedLabel) {
        const maxActualDepth = getLabelMaxDepth(label);
        const maxCompoundDepth = label.pairedLabel.depth ?? 0;

        let diff = maxCompoundDepth - maxActualDepth;

        while (diff > 0) {
            markLevelRangeAsLabeled({ start, end }, levels, currentLevel);
            currentLevel++;
            diff--;
        }
    }

    return currentLevel;
};

/**
 * Takes in an array of labels, and returns a map of labels array as the value and their associated
 * depth as the key.
 */
const mapDepthToLabels = (labels: EnhancedLabel[], map: Map<number, EnhancedLabel[]> = new Map()): Map<number, EnhancedLabel[]> => {
    labels.forEach(label => {
        if (map.has(label.depth)) {
            map.get(label.depth).push(label);
        } else {
            map.set(label.depth, [label]);
        }
        mapDepthToLabels(label.children, map);
    });

    return map;
};

/**
 * Given a label and the levels data structure, it finds the next empty level for
 * the label to be layed out in.
 */
const findNextEmptyLevel = (label: EnhancedLabel, levels: Set<number>[]) => {
    for (let level = 0; level < levels.length; level++) {
        const levelSet = levels[level];
        if ([...levelSet.values()].some(inBetween(label.start, label.end))) {
            if (!levels[level + 1]) {
                levels[level + 1] = new Set();
            }
            continue;
        }

        return level;
    }
};

/**
 * Helper function to set the occupied token indices in the levels sets.
 *
 * @param param0.start Start of the label.
 * @param param0.end End of the label.
 * @param levels Levels sets
 * @param currentLevel Current level to layout the label in.
 */
const markLevelRangeAsLabeled = ({ start, end }: { start: number; end: number }, levels: Set<number>[], level: number): void => {
    if (!levels[level]) {
        levels[level] = new Set();
    }
    for (let tokenIndex = start; tokenIndex <= end; tokenIndex++) {
        levels[level].add(tokenIndex);
    }
};

/// ///////////////////////// Checking conflicts ////////////////////////////

/**
 * Checks for conflicts between labels and predictions
 */
export const checkForConflicts = (tokenizedText: string[], [labels, predictions]: [EnhancedLabel[], EnhancedLabel[]]): boolean[] => {
    const conflicts: boolean[] = tokenizedText.map(_ => false);
    for (const label of labels) {
        if (!isMachineLearned(label.entity.typeId)) {
            continue;
        }
        let overlapFound = false;
        for (const prediction of predictions) {
            if (completeOverlapConflictRules(label, prediction)) {
                label.conflictsWith = prediction;
                prediction.conflictsWith = label;
                markAsConflict(conflicts, label.start, label.end);
                overlapFound = true;
            } else if (partialOverlapConflictRules(label, prediction)) {
                label.conflictsWith = prediction;
                prediction.conflictsWith = label;
                markPartialOverlap(label, prediction, conflicts);
                overlapFound = true;
            }
        }

        if (!overlapFound && !label.predicted) {
            markAsConflict(conflicts, label.start, label.end);
        }
    }

    // predictions that don't have corresponding labels
    for (const prediction of predictions) {
        if (!prediction.conflictsWith) {
            markAsConflict(conflicts, prediction.start, prediction.end);
        }
    }

    return conflicts;
};

/**
 * Marks the conflicts array from the start till the end indices.
 */
const markAsConflict = (conflicts: boolean[], start: number, end: number) => {
    for (let tokenIndex = start; tokenIndex <= end; tokenIndex++) {
        conflicts[tokenIndex] = true;
    }
};

/**
 * Returns true if the two given label and prediction based on the following rules:
 * 1) Both have the same boundary.
 * 2) They are not descendant of each other.
 * 3) They are not the same entity nor do they have the same role.
 */
const completeOverlapConflictRules = (label: EnhancedLabel, prediction: EnhancedLabel): boolean => {
    if (!sameBoundaries(label, prediction)) {
        return false;
    }

    if (isDescendant(label.entity, prediction.entity) || isDescendant(prediction.entity, label.entity)) {
        return false;
    }

    return label.entity.id !== prediction.entity.id || !isSameRole(label.role, prediction.role);
};

/**
 * Returns true if the two given label and prediction based on the following rules:
 * 1) They partially overlap each other.
 * 2) They are not descendant of each other.
 * 3) They are the same entity with the the same role if it exists.
 */
const partialOverlapConflictRules = (label: EnhancedLabel, prediction: EnhancedLabel): boolean => {
    if (!partiallyOverlapLabels(label, prediction)) {
        return false;
    }

    if (isDescendant(label.entity, prediction.entity) || isDescendant(prediction.entity, label.entity)) {
        return false;
    }

    return label.entity.id === prediction.entity.id && isSameRole(label.role, prediction.role);
};

/**
 * Marks the conflicts array with the difference between the label and the prediction boundaries.
 */
const markPartialOverlap = (label: EnhancedLabel, prediction: EnhancedLabel, conflicts: boolean[]) => {
    const startConflictIndices =
        label.start > prediction.start ? { start: prediction.start, end: label.start } : { start: label.start, end: prediction.start };
    const endConflictIndices =
        label.end > prediction.end ? { start: prediction.end, end: label.end } : { start: label.end, end: prediction.end };

    if (label.start - prediction.start !== 0) {
        markAsConflict(conflicts, startConflictIndices.start, startConflictIndices.end - 1);
    }

    if (label.end - prediction.end !== 0) {
        markAsConflict(conflicts, endConflictIndices.start + 1, endConflictIndices.end);
    }
};

const isDescendant = (parent: Entity, entity: Entity) => {
    if (isEntityOfType<SimpleEntity>(parent, ModelType.Simple) || isEntityOfType<CompositeEntity>(parent, ModelType.Composite)) {
        const foundChild = getChildByIdHelper(parent.children, entity.id);

        if (foundChild) {
            return true;
        }
    }

    return false;
};

/// ///////////////////////// Flatten labels ////////////////////////////

/**
 * Flattens labels' children.
 */
export const flattenLabels = (labels: EnhancedLabel[]): EnhancedLabel[] => {
    const flattenedLabels: EnhancedLabel[] = [];
    flattenLabelsHelper(labels, flattenedLabels);

    return flattenedLabels;
};

const flattenLabelsHelper = (labels: EnhancedLabel[], flattenedLabels: EnhancedLabel[] = []) => {
    for (const label of labels) {
        flattenedLabels.push(label);
        flattenLabelsHelper(label.children, flattenedLabels);
    }
};

/// ///////////////////////// Calculate compounds ////////////////////////////

/**
 * Correlates labels and predictions in the same level with each other through the pairedLabel
 * property. And if a prediction doesn't have a matching label we create an artificial soft deleted label
 * that would "guard" the bottom area of that prediction so that no other labels occupy this space.
 */
const calculatePairedLabels = (labels: EnhancedLabel[], predictions: EnhancedLabel[]) => {
    const artificialLabels: EnhancedLabel[] = [];
    for (const prediction of predictions) {
        let formCompound = false;
        for (const label of labels) {
            formCompound =
                (inBetween(label.start, label.end)(prediction.start) ||
                    inBetween(label.start, label.end)(prediction.end) ||
                    inBetween(prediction.start, prediction.end)(label.start) ||
                    inBetween(prediction.start, prediction.end)(label.end)) &&
                label.entity.id === prediction.entity.id;

            if (formCompound) {
                const labelMaxDepth = getLabelMaxDepth(label);
                const predictionMaxDepth = getLabelMaxDepth(prediction);
                label.pairedLabel = {
                    start: Math.min(label.start, prediction.start),
                    end: Math.max(label.end, prediction.end),
                    depth: Math.max(labelMaxDepth, predictionMaxDepth)
                };
                prediction.pairedLabel = {
                    start: Math.min(label.start, prediction.start),
                    end: Math.max(label.end, prediction.end),
                    depth: Math.max(labelMaxDepth, predictionMaxDepth)
                };
                break;
            }
        }

        if (!formCompound) {
            const predictionMaxDepth = getLabelMaxDepth(prediction);

            artificialLabels.push({
                ...prediction,
                children: [],
                pairedLabel: {
                    start: prediction.start,
                    end: prediction.end,
                    depth: predictionMaxDepth
                },
                isSoftDeleted: true
            });
        }
    }

    labels.push(...artificialLabels);
};

/// ///////////////////////// General utilities ////////////////////////////

const filterLabels = (labels: EnhancedLabel[], config: TransformLabelsConfig) => {
    for (let i = labels.length - 1; i >= 0; i--) {
        const label = labels[i];
        let toBeDeleted = false;
        if (config.hidePredictions && label.renderedAs === 'box' && isMachineLearned(label.entity.typeId)) {
            toBeDeleted = true;
        }

        if (config.hideRegexAndPrebuilt && (label.entity.typeId === ModelType.Prebuilt || label.entity.typeId === ModelType.Regex)) {
            toBeDeleted = true;
        }

        if (config.hideSubEntities && label.children) {
            label.children = [];
        }

        if (toBeDeleted) {
            labels.splice(i, 1);
        }
    }
};

const isCompositeParent = (entity: Entity): entity is CompositeEntity => entity.typeId === ModelType.Composite;

const isNDepthParent = (entity: Entity): entity is SimpleEntity =>
    entity.typeId === ModelType.Simple && Boolean((<SimpleEntity>entity).children);

const isSameRole = (role1: EntityRole | undefined, role2: EntityRole | undefined) => role1?.id === role2?.id;

const sameBoundaries = (a: EnhancedLabel, b: EnhancedLabel) => a.start === b.start && b.end === a.end;

const partiallyOverlapLabels = (a: EnhancedLabel, b: EnhancedLabel) =>
    partiallyOverlap(a, b, {
        getStart: (label: EnhancedLabel) => label.start,
        getEnd: (label: EnhancedLabel) => label.end
    });

const sameTokenEntityLabel = (a: EntityLabel, b: EntityLabel) =>
    a.startTokenIndex === b.startTokenIndex && b.endTokenIndex === a.endTokenIndex;
